import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

export const SEX_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
] as const;

export const MARITAL_STATUS_OPTIONS = [
  { value: 'Never married', label: 'Never married' },
  { value: 'Married', label: 'Married' },
  { value: 'Partnered', label: 'Partnered' },
  { value: 'Separated', label: 'Separated' },
  { value: 'Divorced', label: 'Divorced' },
  { value: 'Widowed', label: 'Widowed' },
] as const;

/**
 * Who the plan is for. Split out from the health answers so an agent-entered case can collect
 * identity, stop for the customer's permission, and only then ask anything about their health —
 * storing health data before consent is precisely what the consent flow exists to prevent.
 *
 * `email` is new to this flow. The Quick Plan path collected no address at all, which is why it
 * had no way to ask anyone for anything.
 */
export const quickPlanIdentitySchema = z.object({
  firstName: z.string().trim().min(3, 'First name must be at least 3 characters'),
  lastName: z.string().trim().min(2, 'Last name must be at least 2 characters'),
  email: z.string().trim().email('A valid email address is required').optional().or(z.literal('')),
  birthDate: z.date({ error: 'Birth date is required' }),
});

/** Everything the projection needs, asked only once permission exists. */
export const quickPlanHealthSchema = z.object({
  sex: z.enum(['male', 'female'], {
    error: 'Please select a sex',
  }),
  smokingStatus: z.enum(['never', 'former', 'current'], {
    error: 'Please select smoking status',
  }),
  zipCode: z
    .string()
    .min(1, 'Zip code is required')
    .regex(/^\d{5}$/, 'Must be a 5-digit zip code'),
  maritalStatus: z.string().min(1, 'Marital status is required'),
});

/** The union both halves compose to, so every existing consumer keeps working unchanged. */
export const quickPlanPersonSchema = quickPlanIdentitySchema.merge(quickPlanHealthSchema);

/** Agent-entered cases require the address; everyone else may leave it blank. */
export const quickPlanAgentEnteredIdentitySchema = quickPlanIdentitySchema.extend({
  email: z.string().trim().email('A valid email address is required'),
});

export type QuickPlanIdentityValues = z.infer<typeof quickPlanIdentitySchema>;
export type QuickPlanHealthValues = z.infer<typeof quickPlanHealthSchema>;
export type QuickPlanPersonValues = z.infer<typeof quickPlanPersonSchema>;

/** Which fields `QuickPlanPersonFields` renders. `'all'` is the unchanged single-step form. */
export type QuickPlanFieldStep = 'identity' | 'health' | 'all';

export const quickPlanSchema = quickPlanPersonSchema
  .extend({
    hasPartner: z.boolean().default(false),
    partner: z.any().optional(),
    /** The consented request authorising this person's information to be entered for them. */
    agentEnteredIntakeId: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.hasPartner) {
      if (!data.partner) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Partner details are required',
          path: ['partner'],
        });
      } else {
        const result = quickPlanPersonSchema.safeParse(data.partner);
        if (!result.success) {
          for (const issue of result.error.issues) {
            ctx.addIssue({ ...issue, path: ['partner', ...issue.path] });
          }
        }
      }
    }
  });

export type QuickPlanFormValues = z.infer<typeof quickPlanSchema>;

interface UseQuickPlanFormOptions {
  onSubmit: (data: QuickPlanFormValues) => void;
  /** Which half of the questions this form instance is asking. */
  step?: QuickPlanFieldStep;
  /** Requires the client's email, which agent-entered cases need and nothing else does. */
  requireEmail?: boolean;
  /** Carries answers forward when the flow is split across two mounted forms. */
  defaultValues?: Partial<QuickPlanFormValues>;
}

/**
 * The schema for the half being asked, so `formState.isValid` stays honest.
 *
 * Validating the whole person against a form showing only its identity fields would leave the
 * submit button disabled forever; making the health fields optional and refining at submit time
 * would do the opposite and report a half-filled form as valid.
 */
function schemaForStep(step: QuickPlanFieldStep, requireEmail: boolean) {
  const identity = requireEmail ? quickPlanAgentEnteredIdentitySchema : quickPlanIdentitySchema;

  if (step === 'identity') {
    return identity.extend({
      hasPartner: z.boolean().default(false),
      partner: z.any().optional(),
      agentEnteredIntakeId: z.string().optional(),
    });
  }

  if (step === 'health') {
    return quickPlanHealthSchema.extend({
      hasPartner: z.boolean().default(false),
      partner: z.any().optional(),
      agentEnteredIntakeId: z.string().optional(),
    });
  }

  return quickPlanSchema;
}

const PARTNER_ELIGIBLE_STATUSES = ['Married', 'Partnered'];

export function useQuickPlanForm({
  onSubmit,
  step = 'all',
  requireEmail = false,
  defaultValues,
}: UseQuickPlanFormOptions) {
  const form = useForm<QuickPlanFormValues>({
    resolver: zodResolver(schemaForStep(step, requireEmail) as never),
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      zipCode: '',
      maritalStatus: '',
      hasPartner: false,
      ...defaultValues,
    },
  });

  const hasPartner = form.watch('hasPartner');
  const maritalStatus = form.watch('maritalStatus');
  const canAddPartner = PARTNER_ELIGIBLE_STATUSES.includes(maritalStatus);
  const partnerLabel = maritalStatus === 'Married' ? 'spouse' : 'partner';

  function clearPartner() {
    const { partner, ...values } = form.getValues();
    form.reset({ ...values, hasPartner: false }, { keepDirty: true, keepTouched: true });
  }

  function handleTogglePartner(checked: boolean) {
    if (checked) {
      form.setValue('hasPartner', true, { shouldValidate: true });
      form.setValue('partner.maritalStatus', maritalStatus, { shouldValidate: true });
    } else {
      clearPartner();
    }
  }

  function handleMaritalStatusChange(value: string) {
    form.setValue('maritalStatus', value, { shouldValidate: true });
    if (!PARTNER_ELIGIBLE_STATUSES.includes(value) && hasPartner) {
      clearPartner();
    } else if (hasPartner) {
      form.setValue('partner.maritalStatus', value, { shouldValidate: true });
    }
  }

  function handlePartnerMaritalStatusChange(value: string) {
    form.setValue('partner.maritalStatus', value, { shouldValidate: true });
    form.setValue('maritalStatus', value, { shouldValidate: true });
    if (!PARTNER_ELIGIBLE_STATUSES.includes(value)) {
      clearPartner();
    }
  }

  return {
    form,
    hasPartner,
    canAddPartner,
    partnerLabel,
    handleTogglePartner,
    handleMaritalStatusChange,
    handlePartnerMaritalStatusChange,
    onSubmit: form.handleSubmit(onSubmit),
    isValid: form.formState.isValid,
  };
}
